/**
 * Which policy body a brand renders.
 *
 * Keyed on `brandKey`, with the default as the fallback, so adding a brand's
 * own text is one entry here and one file beside it. Nothing else changes, and
 * no other deployment's policy moves.
 *
 * A brand with no entry keeps the default text. That is deliberate rather than
 * lazy: a deployment whose facts have never been checked should not silently
 * inherit ANOTHER deployment's claims about where its servers are or which AI
 * provider sees its customers' files. The default is the generic description;
 * a brand that needs a precise one writes it.
 */
import type { PolicyBuilder } from './types'
import { buildDefaultSections, LAST_UPDATED as DEFAULT_UPDATED } from './default'
import { buildGnexisSections, LAST_UPDATED as GNEXIS_UPDATED } from './gnexis'

/** A body and the date IT was last reviewed. The two travel together so a
 *  brand can never display someone else's review date. */
export interface Policy {
  build: PolicyBuilder
  lastUpdated: string
}

const BY_BRAND: Record<string, Policy> = {
  gnexis: { build: buildGnexisSections, lastUpdated: GNEXIS_UPDATED },
}

const DEFAULT_POLICY: Policy = { build: buildDefaultSections, lastUpdated: DEFAULT_UPDATED }

export function policyFor(brandKey: string | undefined): Policy {
  return (brandKey && BY_BRAND[brandKey]) || DEFAULT_POLICY
}

export type { PolicyBuilder, PolicyContext, Section } from './types'
